/**
 * Smart group detection for the correlogram.
 *
 * Adapted from flexfoil's polarDetection.ts — generalized for Thread's
 * generic row data (no hardcoded airfoil fields).
 */

import type { RunTableColumn } from "./columnBuilder";
import { isCategory, isHiddenField } from "./columnBuilder";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DataGroup {
  key: string;
  label: string;
  rows: Record<string, unknown>[];
  color: string;
}

export type FieldReasonKind =
  | "categorical_name"
  | "low_cardinality"
  | "source_field"
  | "user_forced"
  | "grouping_keyword";

export interface FieldReason {
  field: string;
  headerName?: string;
  reason: FieldReasonKind;
  uniqueValues: number;
  ratio: number;
  sampleValues: string[];
}

export interface SmartGroupExplanation {
  fieldsUsed: FieldReason[];
  fieldsRejected: FieldReason[];
  groupCount: number;
  totalRows: number;
  gapSplitApplied: boolean;
  gapSplitField?: string;
}

export interface SmartGroupResult {
  groups: DataGroup[];
  explanation: SmartGroupExplanation;
}

export interface SmartGroupOverrides {
  include?: string[];
  exclude?: string[];
}

const GROUP_PALETTE = [
  "#818cf8", "#34d399", "#fbbf24", "#f472b6",
  "#60a5fa", "#a78bfa", "#fb923c", "#2dd4bf",
  "#e879f9", "#38bdf8", "#facc15", "#4ade80",
];

function assignColor(index: number): string {
  return GROUP_PALETTE[index % GROUP_PALETTE.length]!;
}

// Field names containing these keywords are likely grouping candidates
const GROUPING_KEYWORDS = ["group", "type", "config", "case", "variant", "scenario", "condition", "mesh"];

function hasGroupingKeyword(field: string): boolean {
  const lower = field.toLowerCase();
  return GROUPING_KEYWORDS.some((kw) => lower.includes(kw));
}

// ---------------------------------------------------------------------------
// Smart group detection
// ---------------------------------------------------------------------------

function getFieldInfo(
  rows: Record<string, unknown>[],
  field: string,
): { uniqueValues: number; ratio: number; sampleValues: string[] } {
  const unique = new Set(rows.map((r) => r[field]));
  const ratio = rows.length > 0 ? unique.size / rows.length : 1;
  const sampleValues = [...unique]
    .map((v) => (v == null ? "?" : String(v)))
    .slice(0, 5);
  return { uniqueValues: unique.size, ratio, sampleValues };
}

function classifyCategoryFields(
  rows: Record<string, unknown>[],
  columns: RunTableColumn[],
  excludeFields: Set<string>,
  overrides?: SmartGroupOverrides,
): { accepted: FieldReason[]; rejected: FieldReason[] } {
  const accepted: FieldReason[] = [];
  const rejected: FieldReason[] = [];
  const excludeSet = new Set(overrides?.exclude ?? []);
  const includeSet = new Set(overrides?.include ?? []);

  for (const col of columns) {
    if (isHiddenField(col.field)) continue;
    if (excludeFields.has(col.field)) continue;
    if (col.field.startsWith("_") && col.field !== "_source") continue;

    // User explicitly excluded
    if (excludeSet.has(col.field)) continue;

    const info = getFieldInfo(rows, col.field);

    // User explicitly included — always accept
    if (includeSet.has(col.field)) {
      if (info.uniqueValues > 1) {
        accepted.push({
          field: col.field,
          headerName: col.headerName,
          reason: "user_forced",
          ...info,
        });
      }
      continue;
    }

    // _source field
    if (col.field === "_source") {
      if (info.uniqueValues > 1 && info.uniqueValues <= 20) {
        accepted.push({ field: col.field, headerName: col.headerName, reason: "source_field", ...info });
      } else if (info.uniqueValues > 1) {
        rejected.push({ field: col.field, headerName: col.headerName, reason: "source_field", ...info });
      }
      continue;
    }

    // Categorical by name
    if (isCategory(col.field)) {
      if (info.uniqueValues > 1 && info.uniqueValues <= 20) {
        accepted.push({ field: col.field, headerName: col.headerName, reason: "categorical_name", ...info });
      } else if (info.uniqueValues > 1) {
        rejected.push({ field: col.field, headerName: col.headerName, reason: "categorical_name", ...info });
      }
      continue;
    }

    // Grouping keyword in field name
    if (hasGroupingKeyword(col.field)) {
      if (info.uniqueValues > 1 && info.uniqueValues <= 20 && info.ratio < 0.5) {
        accepted.push({ field: col.field, headerName: col.headerName, reason: "grouping_keyword", ...info });
      } else if (info.uniqueValues > 1 && info.uniqueValues <= 30) {
        rejected.push({ field: col.field, headerName: col.headerName, reason: "grouping_keyword", ...info });
      }
      continue;
    }

    // Low cardinality numeric
    if (info.ratio < 0.15 && info.uniqueValues > 1 && info.uniqueValues <= 10) {
      accepted.push({ field: col.field, headerName: col.headerName, reason: "low_cardinality", ...info });
    } else if (info.ratio < 0.3 && info.uniqueValues > 1 && info.uniqueValues <= 15) {
      // Near-miss — show as rejected candidate
      rejected.push({ field: col.field, headerName: col.headerName, reason: "low_cardinality", ...info });
    }
  }

  // If too many accepted fields would produce an explosion of groups, prune
  // Keep only the most discriminative fields (fewest unique values first)
  if (accepted.length > 3) {
    const compoundUniques = estimateCompoundGroups(rows, accepted.map((a) => a.field));
    if (compoundUniques > 30) {
      // Sort by unique values ascending — prefer fewer unique values
      const sorted = [...accepted].sort((a, b) => a.uniqueValues - b.uniqueValues);
      const kept: FieldReason[] = [];
      let estimated = 1;
      for (const f of sorted) {
        if (estimated * f.uniqueValues <= 30 || kept.length === 0) {
          kept.push(f);
          estimated *= f.uniqueValues;
        } else {
          rejected.push(f);
        }
      }
      return { accepted: kept, rejected };
    }
  }

  return { accepted, rejected };
}

function estimateCompoundGroups(rows: Record<string, unknown>[], fields: string[]): number {
  const keys = new Set(rows.map((r) => fields.map((f) => String(r[f] ?? "")).join("|")));
  return keys.size;
}

function buildInvariantKey(row: Record<string, unknown>, fields: string[]): string {
  return fields.map((f) => String(row[f] ?? "")).join("|");
}

function formatValue(_field: string, value: unknown): string {
  if (value == null) return "?";
  if (typeof value === "number") {
    if (Math.abs(value) >= 1e6 || (Math.abs(value) < 0.01 && value !== 0)) {
      return value.toExponential(1);
    }
    return Number.isInteger(value) ? value.toLocaleString() : value.toPrecision(4);
  }
  const s = String(value);
  return s.length > 24 ? s.slice(0, 21) + "…" : s;
}

function shortenFieldName(field: string): string {
  if (field === "_source") return "";
  const parts = field.split("_");
  if (parts.length <= 2) return field;
  // Drop common noise suffixes/prefixes and abbreviate
  const noise = new Set(["units", "f360", "f360s", "deg", "ft", "m"]);
  const meaningful = parts.filter((p) => !noise.has(p.toLowerCase()));
  return meaningful.length > 0 ? meaningful.join("_") : field;
}

const MAX_LABEL_FIELDS = 3;

/**
 * Build concise labels that only show the most discriminative
 * varying fields (capped at MAX_LABEL_FIELDS).
 */
function buildSmartLabels(
  groups: { meta: Record<string, unknown> }[],
  groupFields: string[],
  excludeFromLabels: Set<string>,
): string[] {
  if (groups.length === 0) return [];
  if (groups.length === 1) {
    const first = groups[0]!.meta;
    const nameField = groupFields.find((f) => f === "_source" || f === "name");
    return [nameField ? String(first[nameField]) : "All"];
  }

  const varyingFields = groupFields.filter((field) => {
    if (excludeFromLabels.has(field)) return false;
    const unique = new Set(groups.map((g) => String(g.meta[field] ?? "")));
    return unique.size > 1;
  });

  // Prioritize: _source/name first, then fewest unique values (most discriminative)
  const ranked = [...varyingFields].sort((a, b) => {
    const priority = (f: string) => (f === "_source" || f === "name" ? -1 : 0);
    const p = priority(a) - priority(b);
    if (p !== 0) return p;
    const uA = new Set(groups.map((g) => String(g.meta[a] ?? ""))).size;
    const uB = new Set(groups.map((g) => String(g.meta[b] ?? ""))).size;
    return uA - uB;
  });

  const shown = ranked.slice(0, MAX_LABEL_FIELDS);

  return groups.map((g) => {
    const parts = shown.map((f) => {
      const val = formatValue(f, g.meta[f]);
      const label = f === "_source" ? "" : `${shortenFieldName(f)}=`;
      return `${label}${val}`;
    });
    if (parts.length === 0) {
      const nameField = groupFields.find((f) => f === "_source" || f === "name");
      return nameField ? String(g.meta[nameField]) : "Group";
    }
    return parts.join(", ");
  });
}

/**
 * Split a sorted group by large gaps in a numeric field.
 * Uses IQR-based threshold for robustness.
 * Only applies to groups of 6+ rows.
 */
function splitByGap(
  rows: Record<string, unknown>[],
  sortField: string,
): Record<string, unknown>[][] {
  if (rows.length < 6) return [rows];

  const sorted = [...rows].sort((a, b) => {
    const av = a[sortField];
    const bv = b[sortField];
    if (typeof av === "number" && typeof bv === "number") return av - bv;
    return 0;
  });

  const steps: number[] = [];
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]![sortField];
    const curr = sorted[i]![sortField];
    if (typeof prev === "number" && typeof curr === "number") {
      steps.push(curr - prev);
    }
  }
  if (steps.length === 0) return [sorted];

  // Use IQR-based threshold instead of simple 3x median
  const sortedSteps = [...steps].sort((a, b) => a - b);
  const q1 = sortedSteps[Math.floor(sortedSteps.length * 0.25)]!;
  const q3 = sortedSteps[Math.floor(sortedSteps.length * 0.75)]!;
  const iqr = q3 - q1;
  const threshold = iqr > 0 ? q3 + 2.5 * iqr : sortedSteps[Math.floor(sortedSteps.length / 2)]! * 3;

  const segments: Record<string, unknown>[][] = [[]];
  segments[0]!.push(sorted[0]!);
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]![sortField];
    const curr = sorted[i]![sortField];
    if (typeof prev === "number" && typeof curr === "number" && curr - prev > threshold) {
      segments.push([]);
    }
    segments[segments.length - 1]!.push(sorted[i]!);
  }

  return segments.filter((s) => s.length > 0);
}

/**
 * Auto-detect groups from data using invariant categorical fields.
 *
 * Returns groups along with an explanation of why each field was chosen,
 * and which fields were considered but rejected.
 *
 * Accepts optional overrides to force-include or exclude specific fields.
 */
export function detectSmartGroups(
  rows: Record<string, unknown>[],
  columns: RunTableColumn[],
  plottedFields: string[] = [],
  overrides?: SmartGroupOverrides,
): SmartGroupResult {
  if (rows.length === 0) {
    return {
      groups: [],
      explanation: {
        fieldsUsed: [],
        fieldsRejected: [],
        groupCount: 0,
        totalRows: 0,
        gapSplitApplied: false,
      },
    };
  }

  const excluded = new Set(plottedFields);
  const { accepted, rejected } = classifyCategoryFields(rows, columns, excluded, overrides);
  const groupFields = accepted.map((a) => a.field);

  if (groupFields.length === 0) {
    return {
      groups: [{ key: "all", label: `All (${rows.length})`, rows, color: assignColor(0) }],
      explanation: {
        fieldsUsed: [],
        fieldsRejected: rejected,
        groupCount: 1,
        totalRows: rows.length,
        gapSplitApplied: false,
      },
    };
  }

  const compoundGroups = new Map<string, Record<string, unknown>[]>();
  for (const row of rows) {
    const key = buildInvariantKey(row, groupFields);
    if (!compoundGroups.has(key)) compoundGroups.set(key, []);
    compoundGroups.get(key)!.push(row);
  }

  const numericFields = columns
    .filter((c) => !isCategory(c.field) && !isHiddenField(c.field) && !c.field.startsWith("_"))
    .map((c) => c.field);
  const sortField = numericFields[0];

  let gapSplitApplied = false;
  const allSegments: { key: string; meta: Record<string, unknown>; rows: Record<string, unknown>[]; subIndex: number; totalSubs: number }[] = [];

  for (const [key, groupRows] of [...compoundGroups.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    const meta: Record<string, unknown> = {};
    for (const f of groupFields) meta[f] = groupRows[0]![f];

    const shouldSplit = sortField && groupRows.length >= 6 && typeof groupRows[0]?.[sortField] === "number";
    const segments = shouldSplit ? splitByGap(groupRows, sortField) : [groupRows];

    if (segments.length > 1) gapSplitApplied = true;

    for (let i = 0; i < segments.length; i++) {
      allSegments.push({
        key: segments.length > 1 ? `${key}__seg${i}` : key,
        meta,
        rows: segments[i]!,
        subIndex: i,
        totalSubs: segments.length,
      });
    }
  }

  const labels = buildSmartLabels(allSegments, groupFields, excluded);

  const groups = allSegments.map((seg, i) => {
    let label = labels[i]!;
    if (seg.totalSubs > 1) label += ` (${seg.subIndex + 1})`;
    return { key: seg.key, label, rows: seg.rows, color: assignColor(i) };
  });

  return {
    groups,
    explanation: {
      fieldsUsed: accepted,
      fieldsRejected: rejected,
      groupCount: groups.length,
      totalRows: rows.length,
      gapSplitApplied,
      gapSplitField: gapSplitApplied ? sortField : undefined,
    },
  };
}

/**
 * Group rows by a single field value.
 */
export function groupByField(
  rows: Record<string, unknown>[],
  field: string,
): DataGroup[] {
  const map = new Map<string, Record<string, unknown>[]>();
  for (const row of rows) {
    const val = String(row[field] ?? "");
    if (!map.has(val)) map.set(val, []);
    map.get(val)!.push(row);
  }

  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([val, groupRows], i) => ({
      key: val,
      label: `${val} (${groupRows.length})`,
      rows: groupRows,
      color: assignColor(i),
    }));
}

// ---------------------------------------------------------------------------
// Feedback parsing
// ---------------------------------------------------------------------------

/**
 * Parse user feedback text into override adjustments.
 *
 * Understands simple intents like:
 * - "ignore mach" / "remove mach" → exclude the field
 * - "group by reynolds" / "add reynolds" → include the field
 * - "too many groups" → signal to reduce fields
 * - "too few groups" → signal to add more fields
 */
export function parseFeedback(
  feedback: string,
  allFields: string[],
  currentExplanation: SmartGroupExplanation,
): SmartGroupOverrides & { hint?: string } {
  const lower = feedback.toLowerCase().trim();
  const include: string[] = [];
  const exclude: string[] = [];
  let hint: string | undefined;

  // Match field names in the feedback
  const fieldMap = new Map<string, string>();
  for (const f of allFields) {
    fieldMap.set(f.toLowerCase(), f);
    // Also match without underscores (e.g., "mach number" matches "mach_number")
    fieldMap.set(f.toLowerCase().replace(/_/g, " "), f);
    // Short version: just the last part after underscore
    const parts = f.split("_");
    if (parts.length > 1) {
      fieldMap.set(parts[parts.length - 1]!.toLowerCase(), f);
    }
  }

  // "ignore X" / "remove X" / "don't group by X" / "exclude X"
  const ignorePatterns = [
    /(?:ignore|remove|drop|exclude|don'?t\s+(?:use|group\s+by))\s+["']?([a-z0-9_ ]+)["']?/gi,
  ];
  for (const pat of ignorePatterns) {
    let m;
    while ((m = pat.exec(lower)) !== null) {
      const candidate = m[1]!.trim();
      const match = fieldMap.get(candidate);
      if (match) exclude.push(match);
    }
  }

  // "group by X" / "add X" / "include X" / "use X"
  const includePatterns = [
    /(?:group\s+by|add|include|use)\s+["']?([a-z0-9_ ]+)["']?/gi,
  ];
  for (const pat of includePatterns) {
    let m;
    while ((m = pat.exec(lower)) !== null) {
      const candidate = m[1]!.trim();
      const match = fieldMap.get(candidate);
      if (match) include.push(match);
    }
  }

  // "too many groups"
  if (/too\s+many/i.test(lower)) {
    hint = "reduce";
    // Remove the field with the most unique values
    if (currentExplanation.fieldsUsed.length > 1) {
      const worst = [...currentExplanation.fieldsUsed].sort((a, b) => b.uniqueValues - a.uniqueValues)[0];
      if (worst && !exclude.includes(worst.field)) {
        exclude.push(worst.field);
      }
    }
  }

  // "too few groups"
  if (/too\s+few/i.test(lower)) {
    hint = "expand";
    // Add the most promising rejected field
    if (currentExplanation.fieldsRejected.length > 0) {
      const best = [...currentExplanation.fieldsRejected].sort((a, b) => a.uniqueValues - b.uniqueValues)[0];
      if (best && !include.includes(best.field)) {
        include.push(best.field);
      }
    }
  }

  return {
    include: include.length > 0 ? include : undefined,
    exclude: exclude.length > 0 ? exclude : undefined,
    hint,
  };
}

// ---------------------------------------------------------------------------
// IQR outlier removal
// ---------------------------------------------------------------------------

/**
 * Remove rows where any numeric field falls outside 1.5*IQR fences.
 */
export function removeOutliers(
  rows: Record<string, unknown>[],
  numericFields: string[],
  factor = 1.5,
): Record<string, unknown>[] {
  if (rows.length < 4) return rows;

  const fences = new Map<string, { lo: number; hi: number }>();

  for (const field of numericFields) {
    const vals = rows
      .map((r) => r[field])
      .filter((v): v is number => typeof v === "number" && Number.isFinite(v))
      .sort((a, b) => a - b);

    if (vals.length < 4) continue;

    const q1 = vals[Math.floor(vals.length * 0.25)]!;
    const q3 = vals[Math.floor(vals.length * 0.75)]!;
    const iqr = q3 - q1;
    if (iqr <= 0) continue;

    fences.set(field, { lo: q1 - factor * iqr, hi: q3 + factor * iqr });
  }

  if (fences.size === 0) return rows;

  return rows.filter((row) => {
    for (const [field, { lo, hi }] of fences) {
      const v = row[field];
      if (typeof v === "number" && (v < lo || v > hi)) return false;
    }
    return true;
  });
}
